<template>
  <div class="hero">
    <video
      class="video"
      :src="videoUrl"
      autoplay
      muted
      loop
      playsinline
    />
    <div class="heading">
      <RichText
        :data="pageContent?.find((item) => item.key === 'title')?.value"
      />
      <RichText
        :data="pageContent?.find((item) => item.key === 'subtitle')?.value"
      />
    </div>
    <div class="searchbar-container">
      <BookingSearchBar is-booking-home />
    </div>
  </div>
</template>

<script setup>
import { onMounted, ref, watchEffect } from "vue";
import { useQuery } from "villus";
import { useMediaQuery } from "@vueuse/core";

import { useBookingStore } from "../stores/booking";
import BookingSearchBar from "./BookingSearchBar.vue";
import RichText from "./RichText.vue";
import { useTranslationsStore } from "../stores/translations";
import { graphqlGetBookingHomepage } from "../utils/queryGraphql";

const translationsStore = useTranslationsStore();
const bookingStore = useBookingStore();

const { data } = useQuery({
  query: graphqlGetBookingHomepage,
  variables: {
    language: translationsStore.getShopifyLocale(),
  },
});

const transformData = (data) => {
  return data.metaobjects?.edges[0].node.fields;
};

const pageContent = ref(null);
const hasBackgroundImage = ref(false);
const hasBackgroundVideo = ref(false);
const videoUrl = ref(null);

const isLargeScreen = useMediaQuery("(min-width: 1281px)");

watchEffect(() => {
  if (data.value) {
    pageContent.value = transformData(data.value);

    // background image
    const bg = pageContent.value?.find(
      (item) => item.key === "background_image"
    )?.reference?.image?.url;

    if (bg) {
      hasBackgroundImage.value = true;
      document.body.style.setProperty("--booking-home-bg", `url(${bg})`);
    }

    // background video for large screens
    const largeVideoSources = pageContent.value?.find(
      (item) => item.key === "background_video_large"
    )?.reference?.sources;

    if (largeVideoSources.length > 0 && isLargeScreen.value) {
      hasBackgroundVideo.value = true;
      videoUrl.value = largeVideoSources.filter((source) => {
        return source.format === "mp4";
      })[0].url;
    }

    // background video for small screens
    const smallVideoSources = pageContent.value?.find(
      (item) => item.key === "background_video_small"
    )?.reference?.sources;

    if (smallVideoSources.length > 0 && !isLargeScreen.value) {
      hasBackgroundVideo.value = true;
      videoUrl.value = smallVideoSources.filter((source) => {
        return source.format === "mp4";
      })[0].url;
    }
  }
});

onMounted(() => {
  bookingStore.resetStore();
  bookingStore.journeyType = "center"; // set default manually
});
</script>

<style scoped>
.hero {
  display: grid;
  grid-template-rows: 1fr;
  height: calc(100vh - var(--announcement-bar-height) - var(--header-height));
  background-color: rgb(var(--brand-primary) / 1);
  background-image: var(--booking-home-bg);
  background-size: cover;
  position: relative;
}
@media screen and (min-width: 700px) and (max-width: 999px) {
  .hero {
    margin-bottom: 250px;
    height: calc(100vh - var(--announcement-bar-height) - var(--header-height));
  }
}
.heading {
  z-index: 1;
  color: rgb(var(--brand-white) / 1);
  text-align: center;
  align-self: end;
  padding: 2rem 1.5rem;
  max-width: 30rem;
  margin-inline: auto;
  @media screen and (min-width: 1000px) {
    padding: 0;
    margin-block-end: 3rem;
  }
}
.heading h1 {
  font-family: var(--heading-font-family), serif;
  font-size: 2rem;
  font-style: normal;
  line-height: 1;
  font-weight: 400;
  margin: 0 0 1.5rem 0;
  @media screen and (min-width: 1000px) {
    font-size: 3.25rem;
  }
}
:deep(.heading h1 em) {
  font-family: var(--heading-italic-font-family), sans-serif;
  font-weight: var(--heading-italic-font-weight);
  display: block;
  font-style: normal;
  text-transform: uppercase;
}
.heading p {
  letter-spacing: 0.44px;
  font:
    1rem var(--text-font-family),
    sans-serif;
}
.searchbar-container {
  z-index: 1;
  margin-bottom: 2.5rem;
  @media screen and (min-width: 1000px) {
    margin-bottom: 4.5rem;
  }
}
.video {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
  object-fit: cover;
}
</style>
